import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IContextSetting, LaboratorySample, NetworkConstants, statusColorsLabSampleGR } from 'diwimamodelweb/lib';
import { deepCopy, removeUndefined } from 'diwimamodelweb/lib/utils/obejct-diff';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { LabSampleService } from '../lab-sample.service';
import { LaboratorySampleForm } from '../model/form';
import { UserFeedbackService } from '../user-feedback.service';
import { SettingsService } from '../settings.service';
import { buildCustomDiff } from 'diwimamodelweb/lib/utils/obejct-diff';

@Component({
  selector: 'app-lab-sample-form',
  templateUrl: './lab-sample-form.component.html',
  styleUrls: ['./lab-sample-form.component.css', '../../app.component.scss']
})
export class LabSampleFormComponent implements OnInit {

  @Input() data: LaboratorySampleForm
  @Input() formBuilder: FormBuilder
  @Input() userFeedbackService: UserFeedbackService
  @Input() translateService: TranslateService
  @Input() objectService: LabSampleService

  labSampleForm: FormGroup
  statusColorsLabSampleGR = statusColorsLabSampleGR
  oldLabSample: LaboratorySample

  dropdownType: IContextSetting[]
  dropdownStatus: IContextSetting[]
  dropdownLocation: IContextSetting[]

  lsUpdateRight = false
  lsDeleteRight = false

  get canSave(): boolean {
    return this.lsUpdateRight && !this.labSampleForm?.invalid
  }

  get canDelete(): boolean {
    return this.lsDeleteRight && !this.labSampleForm?.invalid
  }

  constructor(private settingsService: SettingsService) { }

  private loadCrudRights(): void {
    const ctxId = this.settingsService?.selectedUserContext$?.value?.identifier
    const rights = ctxId
      ? this.settingsService.rightsPercontext?.get(ctxId)?.[NetworkConstants.COLLECTION_LABSAMPLE]
      : undefined
    this.lsUpdateRight = !!rights?.['U']
    this.lsDeleteRight = !!rights?.['D']
  }

  ngOnInit(): void {
    this.loadCrudRights()
    if (this.data.data) this.oldLabSample = deepCopy(this.data.data, this.data.data.replacerFn)
    this.dropdownLocation = this.data.contextSettings.get(NetworkConstants.SETTING_LAB_SAMPLE_LOCATION)?.values
    this.dropdownStatus = this.data.contextSettings.get(NetworkConstants.SETTING_LAB_SAMPLE_STATUS)?.values
    this.dropdownType = this.data.contextSettings.get(NetworkConstants.SETTING_LAB_SAMPLE_TYPE)?.values
    
    this.labSampleForm = this.formBuilder.group({
      materialID: [],
      type: [],
      status: [],
      currentLocation: [],
      result: [],
      comment: [],
      nvState:[],
      examinations:[],
      hiTierState:[],
      hiTierError:[],
      hiTierErrorCode:[], 
      nvError:[],
      nvErrorCode:[]
    })
    if (this.data.data) this.initializeWithData()
    this.labSampleForm.controls.materialID.disable()

    if (!this.lsUpdateRight) this.labSampleForm.disable({ emitEvent: false })

    this.labSampleForm.valueChanges.subscribe((value) => {
      // get titles ... 
      const {materialID, type, status, currentLocation, result, comment, nvState, examinations, hiTierState, hiTierError, hiTierErrorCode, nvError, nvErrorCode} = this.labSampleForm.getRawValue()
      const up = {
        type: type?.identifier,
        typeTitle: type?.title,
        status: status?.identifier,
        statusTitle: status?.title,
        currentLocation: currentLocation?.identifier,
        currentLocationTitle: currentLocation?.title,
        comment,
        result,
        nvState,
        examinations,
        hiTierState,
        hiTierError,
        hiTierErrorCode, 
        nvError,
        nvErrorCode
      };
      (this.data.data as LaboratorySample).setFromAny(up)

    })
    this.data.onNavBack = this.onCancle
  }

  initializeWithData() {
    const {materialID, type, status, currentLocation, result, comment,nvState, examinations, hiTierState, hiTierError, hiTierErrorCode, nvError, nvErrorCode} = this.data.data
    const settType = this.dropdownType?.find(el => el.identifier == type)
    const settLocation = this.dropdownLocation?.find(el => el.identifier == currentLocation)
    const settStatus = this.dropdownStatus?.find(el => el.identifier == status)
    
    this.labSampleForm.patchValue({
      materialID, type: settType, status: settStatus, currentLocation: settLocation, result, comment, nvState,
      examinations:removeUndefined(examinations), hiTierState, hiTierError, hiTierErrorCode, nvError, nvErrorCode
    })
  }

  async onSubmit() {
    if (!this.canSave) {
      if (!this.lsUpdateRight) {
        const message = this.translateService.instant('LS_not_allowed_update')
        this.userFeedbackService.showSnackBar.emit(message)
      }
      return
    }
    try {
      this.userFeedbackService.loading.emit(true)
      const newLabSample = deepCopy(this.data.data, this.data.data.replacerFn)
      await this.objectService.updateLabSample(this.oldLabSample, newLabSample)
      const sbSuccessSave = this.translateService.instant('changes_saved')
      this.userFeedbackService.loading.emit(false)
      this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
      this.data.onCancle()
    } catch (error) {
      console.error(error)
      this.userFeedbackService.loading.emit(false)
      this.userFeedbackService.showDialog.emit(
        {
          entryComponent: GenericDialogComponent,
          title: "Error",
          message: error?.error?.message ? error?.error?.message : JSON.stringify(error),
        }
      )
    }
  }

  onSuccessfullySubmitted() {
    this.data.onSuccessfullySubmitted()
  }

  onCancle = async () => {
    const newLabSample = deepCopy(this.data.data, this.data.data.replacerFn)
    const diff = buildCustomDiff(this.oldLabSample, newLabSample)
    if (Object.keys(diff).length == 0) this.data.onCancle()
    else {
      const title = this.translateService.instant('title_unsaved_changes')
      const message = this.translateService.instant('msg_unsaved_changes')
      this.userFeedbackService.showDialog.emit(
        {
          entryComponent: GenericDialogComponent,
          title,
          message,
          cancleCallback: async () => {
            const sbChangesDismissed = this.translateService.instant('changes_dismissed')
            this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
            this.data.onCancle()
          },
          positiveCallback: async ()=> {
            await this.onSubmit()
            this.data.onSuccessfullySubmitted
          }
        }
      )
    }
  }


  async onDelete(){
    if (!this.canDelete) return
    
    const title = this.translateService.instant('delete_title')
    const message = this.translateService.instant('delete_message')
    this.userFeedbackService.loading.emit(false)
    this.userFeedbackService.showDialog.emit({
      entryComponent: GenericDialogComponent,
      title: title,
      message: message,
      cancleCallback: async ()=> {
        const sbCancelDelete = this.translateService.instant('delete_canceled') 
        this.userFeedbackService.showSnackBar.emit(sbCancelDelete)
      },
      positiveCallback: async ()=>{
        try{
         
          this.userFeedbackService.loading.emit(true)
          const sbSuccessDelete = this.translateService.instant('delete_completed')
          
          await this.data.onDelete()
          this.userFeedbackService.loading.emit(false)
          this.userFeedbackService.showSnackBar.emit(sbSuccessDelete)
         
        }catch(errorDelete){
          console.log(errorDelete)
          const sbErrorDelete = this.translateService.instant('delete_error')
          this.userFeedbackService.showSnackBar.emit(sbErrorDelete)
        }
      } 
    })
    
  }

}